// Settings → Security → "Two-factor authentication" card. Status display +
// enable / disable / regenerate-recovery-codes dialogs. Talks to
// /api/auth/mfa/* (see api/core/auth/mfa.routes.ts for the exact contracts).
//
// Secrets discipline: the otpauth secret and the recovery codes are shown
// EXACTLY ONCE — immediately after enroll/confirm or regenerate — and are
// never re-fetched, re-rendered, or logged after the dialog that produced
// them closes.
import * as React from 'react';
import QRCode from 'qrcode';
import { toast } from 'sonner';
import { ShieldCheck, ShieldOff, Copy, Download, KeyRound } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { GlassCard, Eyebrow, Pill } from '../../design-system/pds';
import { api, ApiError } from '../api-client';
import { cn } from '../../lib/utils';
import { useAuth } from './AuthProvider';

interface MfaStatus {
  enabled: boolean;
  confirmed_at: string | null;
  recovery_codes_left: number;
}

function errMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return String((err as any)?.message ?? err);
}

/* ─────────────────────────── card ─────────────────────────── */

export function MfaSettingsCard() {
  const { identity } = useAuth();
  // Admins have no /regenerate-recovery-codes route (the server rejects it
  // with 403 not_supported_for_admins — they rotate by disabling and
  // re-enrolling instead), so hide the action rather than let it 403.
  const isAdmin = identity?.type === 'super_admin';
  const [status, setStatus] = React.useState<MfaStatus | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [enrollOpen, setEnrollOpen] = React.useState(false);
  const [disableOpen, setDisableOpen] = React.useState(false);
  const [regenOpen, setRegenOpen] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api<MfaStatus>('/api/auth/mfa/status');
      setStatus(r);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  return (
    <GlassCard padding="p-6 sm:p-7">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <Eyebrow className="mb-1.5">Two-factor authentication</Eyebrow>
          <p className="text-[14px] font-semibold text-ink dark:text-white flex items-center gap-2">
            {loading ? 'Loading…' : status?.enabled ? 'Enabled' : 'Not enabled'}
            {!loading && status?.enabled && (
              <Pill variant="lime" size="sm">Active</Pill>
            )}
          </p>
          <p className="text-[12.5px] text-ink/60 dark:text-white/55 mt-1.5 max-w-md leading-relaxed">
            {status?.enabled ? (
              <>
                You'll need your authenticator app each time you sign in.
                {status.confirmed_at && (
                  <> Enabled since {new Date(status.confirmed_at).toLocaleDateString()}.</>
                )}{' '}
                {status.recovery_codes_left} recovery code{status.recovery_codes_left === 1 ? '' : 's'} left.
              </>
            ) : (
              'Add an authenticator app (Google Authenticator, 1Password, Authy…) as a second sign-in step, free.'
            )}
          </p>
        </div>

        {!loading && (
          status?.enabled ? (
            <div className="flex flex-wrap items-center gap-2 shrink-0">
              {!isAdmin && (
                <Button variant="secondary" size="sm" onClick={() => setRegenOpen(true)}>
                  <KeyRound className="h-3.5 w-3.5" />
                  New recovery codes
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={() => setDisableOpen(true)}>
                <ShieldOff className="h-3.5 w-3.5" />
                Disable
              </Button>
            </div>
          ) : (
            <Button size="sm" onClick={() => setEnrollOpen(true)} className="shrink-0">
              <ShieldCheck className="h-3.5 w-3.5" />
              Enable
            </Button>
          )
        )}
      </div>

      {enrollOpen && (
        <EnrollDialog
          open={enrollOpen}
          onOpenChange={setEnrollOpen}
          onEnabled={() => { load(); }}
        />
      )}
      {disableOpen && (
        <DisableDialog
          open={disableOpen}
          onOpenChange={setDisableOpen}
          onDisabled={() => { load(); }}
        />
      )}
      {regenOpen && (
        <RegenerateDialog
          open={regenOpen}
          onOpenChange={setRegenOpen}
          onDone={() => { load(); }}
        />
      )}
    </GlassCard>
  );
}

/* ─────────────────────────── recovery codes reveal ─────────────────────────── */

function RecoveryCodesReveal({
  codes, onDone, stored, setStored, blocked,
}: {
  codes: string[]; onDone: () => void;
  stored: boolean; setStored: (v: boolean) => void;
  /** True right after a dismissal attempt was blocked — flashes the checkbox label. */
  blocked?: boolean;
}) {
  function copyAll() {
    navigator.clipboard?.writeText(codes.join('\n')).then(
      () => toast.success('Recovery codes copied'),
      () => toast.error('Could not copy — select and copy manually'),
    );
  }

  function download() {
    const blob = new Blob(
      [`Worqly recovery codes\nGenerated ${new Date().toISOString()}\nEach code can be used once.\n\n${codes.join('\n')}\n`],
      { type: 'text/plain' },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'worqly-recovery-codes.txt';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-4">
      <DialogHeader>
        <DialogTitle>Save your recovery codes</DialogTitle>
        <DialogDescription>
          Store these now — shown once. Each code signs you in one time if you lose access to your
          authenticator app. Treat them like passwords.
        </DialogDescription>
      </DialogHeader>

      <div className="grid grid-cols-2 gap-2 rounded-2xl bg-ink/5 dark:bg-white/5 p-4 font-mono text-[13.5px] tabular-nums">
        {codes.map((c) => (
          <div key={c} className="text-ink dark:text-white">{c}</div>
        ))}
      </div>

      <div className="flex gap-2">
        <Button type="button" variant="secondary" size="sm" onClick={copyAll} className="flex-1">
          <Copy className="h-3.5 w-3.5" />
          Copy all
        </Button>
        <Button type="button" variant="secondary" size="sm" onClick={download} className="flex-1">
          <Download className="h-3.5 w-3.5" />
          Download .txt
        </Button>
      </div>

      <label
        className={cn(
          'flex items-start gap-2.5 text-[12.5px] cursor-pointer transition-colors',
          blocked ? 'text-rose-600 dark:text-rose-300 font-semibold' : 'text-ink/70 dark:text-white/65',
        )}
      >
        <input
          type="checkbox"
          checked={stored}
          onChange={(e) => setStored(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-ink/30 dark:border-white/30 accent-coral"
        />
        {blocked ? "Confirm you've stored the codes first" : "I've stored these codes somewhere safe."}
      </label>

      <DialogFooter>
        <Button type="button" disabled={!stored} onClick={onDone}>
          Done
        </Button>
      </DialogFooter>
    </div>
  );
}

/* ─────────────────────────── enroll dialog ─────────────────────────── */

type EnrollStep = 'loading' | 'setup' | 'codes' | 'error';

function EnrollDialog({
  open, onOpenChange, onEnabled,
}: { open: boolean; onOpenChange: (v: boolean) => void; onEnabled: () => void }) {
  const [step, setStep] = React.useState<EnrollStep>('loading');
  const [otpauthUrl, setOtpauthUrl] = React.useState('');
  const [manualKey, setManualKey] = React.useState('');
  const [qrDataUrl, setQrDataUrl] = React.useState('');
  const [code, setCode] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [recoveryCodes, setRecoveryCodes] = React.useState<string[]>([]);
  const [codesStored, setCodesStored] = React.useState(false);
  const [dismissBlocked, setDismissBlocked] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api<{ otpauth_url: string; manual_key: string }>('/api/auth/mfa/enroll', { method: 'POST', body: {} });
        if (cancelled) return;
        setOtpauthUrl(r.otpauth_url);
        setManualKey(r.manual_key);
        const qr = await QRCode.toDataURL(r.otpauth_url);
        if (cancelled) return;
        setQrDataUrl(qr);
        setStep('setup');
      } catch (err) {
        if (cancelled) return;
        setError(errMessage(err));
        setStep('error');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  async function onConfirm(e: React.FormEvent) {
    e.preventDefault();
    if (!code.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await api<{ recovery_codes: string[] }>('/api/auth/mfa/confirm', { method: 'POST', body: { code: code.trim() } });
      setRecoveryCodes(r.recovery_codes);
      setStep('codes');
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  // While the recovery codes are on screen and unconfirmed, dismissal is
  // blocked from every path (Escape, click-outside, the X button, and any
  // programmatic close) — losing them unstored is a lockout footgun.
  function guardDismiss(e: { preventDefault: () => void }) {
    if (step === 'codes' && !codesStored) {
      e.preventDefault();
      setDismissBlocked(true);
      window.setTimeout(() => setDismissBlocked(false), 1500);
    }
  }

  function close(v: boolean) {
    if (!v) {
      if (step === 'codes' && !codesStored) {
        // Belt: even if something reaches onOpenChange directly, don't close.
        setDismissBlocked(true);
        window.setTimeout(() => setDismissBlocked(false), 1500);
        return;
      }
      if (step === 'codes') onEnabled();
    }
    onOpenChange(v);
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        className="max-w-md"
        onEscapeKeyDown={guardDismiss}
        onPointerDownOutside={guardDismiss}
        onInteractOutside={guardDismiss}
      >
        {step === 'loading' && (
          <div className="py-8 text-center text-[13.5px] text-ink/60 dark:text-white/55">Setting up…</div>
        )}

        {step === 'error' && (
          <div className="space-y-4">
            <DialogHeader>
              <DialogTitle>Couldn't start enrollment</DialogTitle>
            </DialogHeader>
            <p className="text-[13px] text-rose-600 dark:text-rose-300">{error}</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
            </DialogFooter>
          </div>
        )}

        {step === 'setup' && (
          <form onSubmit={onConfirm} className="space-y-4">
            <DialogHeader>
              <DialogTitle>Set up two-factor authentication</DialogTitle>
              <DialogDescription>
                Scan this QR code with your authenticator app (Google Authenticator, 1Password, Authy…),
                then enter the 6-digit code it shows.
              </DialogDescription>
            </DialogHeader>

            {qrDataUrl && (
              <div className="flex justify-center">
                <img src={qrDataUrl} alt="Scan with your authenticator app" width={200} height={200} className="rounded-xl ring-1 ring-ink/10 dark:ring-white/15" />
              </div>
            )}

            <div>
              <Label className="text-[11.5px] text-ink/55 dark:text-white/50">Can't scan? Enter this key manually</Label>
              <p className="mt-1 font-mono text-[13px] break-all bg-ink/5 dark:bg-white/5 rounded-lg px-3 py-2 text-ink dark:text-white">
                {manualKey}
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="enroll-code">6-digit code</Label>
              <Input
                id="enroll-code"
                autoFocus
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="tracking-[0.2em] text-center font-mono"
              />
            </div>

            {error && (
              <div className="text-[13px] text-rose-700 dark:text-rose-300 bg-rose-500/10 ring-1 ring-rose-500/20 rounded-2xl px-3 py-2" role="alert">
                {error}
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting || !code.trim()}>
                {submitting ? 'Verifying…' : 'Verify & enable'}
              </Button>
            </DialogFooter>
          </form>
        )}

        {step === 'codes' && (
          <RecoveryCodesReveal
            codes={recoveryCodes}
            onDone={() => close(false)}
            stored={codesStored}
            setStored={setCodesStored}
            blocked={dismissBlocked}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────────────── disable dialog ─────────────────────────── */

function CodeOrRecoveryFields({
  code, setCode, useRecovery, setUseRecovery,
}: {
  code: string; setCode: (v: string) => void;
  useRecovery: boolean; setUseRecovery: (v: boolean) => void;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label htmlFor="mfa-guard-code">{useRecovery ? 'Recovery code' : 'Authentication code'}</Label>
        <button
          type="button"
          onClick={() => { setUseRecovery(!useRecovery); setCode(''); }}
          className="text-[11.5px] font-semibold text-ink/50 dark:text-white/50 hover:text-ink dark:hover:text-white transition-colors"
        >
          {useRecovery ? 'Use authenticator code' : 'Use a recovery code'}
        </button>
      </div>
      <Input
        id="mfa-guard-code"
        inputMode={useRecovery ? 'text' : 'numeric'}
        maxLength={useRecovery ? 12 : 6}
        placeholder={useRecovery ? 'XXXXXXXXXX' : '000000'}
        value={code}
        onChange={(e) => setCode(e.target.value)}
        className={cn(!useRecovery && 'tracking-[0.2em] text-center font-mono')}
      />
    </div>
  );
}

function DisableDialog({
  open, onOpenChange, onDisabled,
}: { open: boolean; onOpenChange: (v: boolean) => void; onDisabled: () => void }) {
  const [password, setPassword] = React.useState('');
  const [code, setCode] = React.useState('');
  const [useRecovery, setUseRecovery] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting || !password || !code.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await api('/api/auth/mfa/disable', {
        method: 'POST',
        body: { password, code: useRecovery ? undefined : code.trim(), recoveryCode: useRecovery ? code.trim() : undefined },
      });
      toast.success('Two-factor authentication disabled');
      onOpenChange(false);
      onDisabled();
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <form onSubmit={onSubmit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Disable two-factor authentication</DialogTitle>
            <DialogDescription>
              Confirm your password and a current code. Your account will only need a password to sign in afterward.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-1.5">
            <Label htmlFor="disable-password">Password</Label>
            <Input
              id="disable-password"
              type="password"
              autoComplete="current-password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <CodeOrRecoveryFields code={code} setCode={setCode} useRecovery={useRecovery} setUseRecovery={setUseRecovery} />

          {error && (
            <div className="text-[13px] text-rose-700 dark:text-rose-300 bg-rose-500/10 ring-1 ring-rose-500/20 rounded-2xl px-3 py-2" role="alert">
              {error}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" variant="destructive" disabled={submitting || !password || !code.trim()}>
              {submitting ? 'Disabling…' : 'Disable'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────────────── regenerate codes dialog ─────────────────────────── */

function RegenerateDialog({
  open, onOpenChange, onDone,
}: { open: boolean; onOpenChange: (v: boolean) => void; onDone: () => void }) {
  const [password, setPassword] = React.useState('');
  const [code, setCode] = React.useState('');
  const [useRecovery, setUseRecovery] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [recoveryCodes, setRecoveryCodes] = React.useState<string[] | null>(null);
  const [codesStored, setCodesStored] = React.useState(false);
  const [dismissBlocked, setDismissBlocked] = React.useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting || !password || !code.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await api<{ recovery_codes: string[] }>('/api/auth/mfa/regenerate-recovery-codes', {
        method: 'POST',
        body: { password, code: useRecovery ? undefined : code.trim(), recoveryCode: useRecovery ? code.trim() : undefined },
      });
      setRecoveryCodes(r.recovery_codes);
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  function guardDismiss(e: { preventDefault: () => void }) {
    if (recoveryCodes && !codesStored) {
      e.preventDefault();
      setDismissBlocked(true);
      window.setTimeout(() => setDismissBlocked(false), 1500);
    }
  }

  function close(v: boolean) {
    if (!v) {
      if (recoveryCodes && !codesStored) {
        // Belt: even if something reaches onOpenChange directly, don't close.
        setDismissBlocked(true);
        window.setTimeout(() => setDismissBlocked(false), 1500);
        return;
      }
      if (recoveryCodes) onDone();
    }
    onOpenChange(v);
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        className="max-w-md"
        onEscapeKeyDown={guardDismiss}
        onPointerDownOutside={guardDismiss}
        onInteractOutside={guardDismiss}
      >
        {recoveryCodes ? (
          <RecoveryCodesReveal
            codes={recoveryCodes}
            onDone={() => close(false)}
            stored={codesStored}
            setStored={setCodesStored}
            blocked={dismissBlocked}
          />
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            <DialogHeader>
              <DialogTitle>Generate new recovery codes</DialogTitle>
              <DialogDescription>
                Your old recovery codes stop working immediately. Confirm your password and a current code.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-1.5">
              <Label htmlFor="regen-password">Password</Label>
              <Input
                id="regen-password"
                type="password"
                autoComplete="current-password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            <CodeOrRecoveryFields code={code} setCode={setCode} useRecovery={useRecovery} setUseRecovery={setUseRecovery} />

            {error && (
              <div className="text-[13px] text-rose-700 dark:text-rose-300 bg-rose-500/10 ring-1 ring-rose-500/20 rounded-2xl px-3 py-2" role="alert">
                {error}
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting || !password || !code.trim()}>
                {submitting ? 'Generating…' : 'Generate new codes'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
